import { FRAUD_DETECTION_CONFIG, getConfigValue } from '../config/fraud-detection-config';

// Export config for backward compatibility
export const FRAUD_CONFIG = FRAUD_DETECTION_CONFIG.rules;
export const FRAUD_WEIGHTS = FRAUD_DETECTION_CONFIG.weights;

// Parse coordinates from various formats
export function parseCoords(coords) {
  if (!coords) return null;
  
  if (typeof coords === 'object' && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
    return coords;
  }
  
  if (typeof coords === 'string') {
    const parts = coords.split(',').map(s => parseFloat(s.trim()));
    if (parts.length === 2 && parts.every(Number.isFinite)) {
      return { lat: parts[0], lng: parts[1] };
    }
  }
  
  return null;
}

// Check if two stations are the same (within epsilon distance)
export function sameStation(coordsA, coordsB) {
  if (!coordsA || !coordsB) return false;
  
  const a = parseCoords(coordsA);
  const b = parseCoords(coordsB);
  
  if (!a || !b) return false;
  
  return Math.abs(a.lat - b.lat) < FRAUD_CONFIG.SAME_STATION_EPS && 
         Math.abs(a.lng - b.lng) < FRAUD_CONFIG.SAME_STATION_EPS;
}

// Calculate Haversine distance in kilometers
export function haversineKm(coordsA, coordsB) {
  const a = parseCoords(coordsA);
  const b = parseCoords(coordsB);
  
  if (!a || !b) return null;
  
  const R = 6371; // Earth's radius in km
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  
  const lat1Rad = a.lat * Math.PI / 180;
  const lat2Rad = b.lat * Math.PI / 180;
  
  const haversineA = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(haversineA), Math.sqrt(1 - haversineA));
  
  return R * c;
}

// Calculate time difference in hours
export function hoursDifference(timeA, timeB) {
  if (!timeA || !timeB) return null;
  
  const dateA = timeA.toDate ? timeA.toDate() : new Date(timeA);
  const dateB = timeB.toDate ? timeB.toDate() : new Date(timeB);
  
  return Math.abs(dateB - dateA) / (1000 * 60 * 60);
}

// Get expected efficiency for vehicle type (placeholder - can be enhanced with actual vehicle data)
export function getExpectedEfficiency(vehicle) {
  // TODO: Implement actual vehicle-based efficiency lookup
  // For now, return default
  return getConfigValue('defaults.EFFICIENCY_KM_PER_L', 3.0);
}

// Get tank capacity for vehicle (placeholder - can be enhanced with actual vehicle data)
export function getTankCapacity(vehicle) {
  // TODO: Implement actual vehicle-based tank capacity lookup
  // For now, return default
  return getConfigValue('defaults.TANK_CAPACITY_L', 200);
}

// Calculate rolling windows for small fills and frequency
export function calculateRollingWindows(transactions, currentIndex) {
  const current = transactions[currentIndex];
  const currentTime = current.fill_time.toDate ? current.fill_time.toDate() : new Date(current.fill_time);
  
  let fillsIn24h = 0;
  let smallCount24h = 0;
  let smallSum24h = 0;
  let smallSameStation12h = false;
  
  // Look at transactions within time windows
  for (let i = 0; i < transactions.length; i++) {
    if (i === currentIndex) continue;
    
    const other = transactions[i];
    const otherTime = other.fill_time.toDate ? other.fill_time.toDate() : new Date(other.fill_time);
    const hoursDiff = Math.abs(currentTime - otherTime) / (1000 * 60 * 60);
    
    // 24-hour window
    if (hoursDiff <= 24) {
      fillsIn24h++;
      
      if (other.liters <= FRAUD_CONFIG.SMALL_L_THRESH) {
        smallCount24h++;
        smallSum24h += other.liters;
      }
    }
    
    // 12-hour window for same station check
    if (hoursDiff <= 12 && 
        current.liters <= FRAUD_CONFIG.SMALL_L_THRESH && 
        other.liters <= FRAUD_CONFIG.SMALL_L_THRESH &&
        sameStation(current.station_coordinates, other.station_coordinates)) {
      smallSameStation12h = true;
    }
  }
  
  return {
    fillsIn24h,
    smallCount24h,
    smallSum24h,
    smallSameStation12h
  };
}

// Apply fraud detection rules with proper null handling
export function applyFraudRules(transaction, prevTransaction, rollingWindows, vehicleBaseline) {
  const flags = [];
  let score = 0;
  
  const liters = parseFloat(transaction.liters) || 0;
  const kmFromLast = transaction.kmFromLast; // Keep as null if missing
  const odoDelta = transaction.odoDelta; // Keep as null if missing
  const efficiency = transaction.efficiencyKmPerL; // Keep as null if missing
  const expectedEff = getExpectedEfficiency(vehicleBaseline);
  const tankCapacity = getTankCapacity(vehicleBaseline);
  
  // Rule 1: NO_MOVE_BIG_FILL - only if we have previous transaction and distance data
  if (prevTransaction && kmFromLast !== null && liters > FRAUD_CONFIG.NO_MOVE_BIG_FILL_L) {
    if (kmFromLast < 5 || sameStation(transaction.station_coordinates, prevTransaction.station_coordinates)) {
      flags.push('NO_MOVE_BIG_FILL');
      score += FRAUD_WEIGHTS.NO_MOVE_BIG_FILL;
    }
  }
  
  // Rule 2: SHORT_HOP_BIG_FILL - only if we have distance data
  if (prevTransaction && kmFromLast !== null && 
      liters > FRAUD_CONFIG.SHORT_HOP_BIG_FILL_L && 
      kmFromLast < FRAUD_CONFIG.SHORT_HOP_KM) {
    flags.push('SHORT_HOP_BIG_FILL');
    score += FRAUD_WEIGHTS.SHORT_HOP_BIG_FILL;
  }
  
  // Rule 3: ODOMETER_ANOMALY - only if we have both odometer readings
  if (prevTransaction && odoDelta !== null) {
    if (odoDelta < 0 || (odoDelta < FRAUD_CONFIG.ODO_MIN_DELTA_KM && liters > FRAUD_CONFIG.NO_MOVE_BIG_FILL_L)) {
      flags.push('ODOMETER_ANOMALY');
      score += FRAUD_WEIGHTS.ODOMETER_ANOMALY;
    }
  }
  
  // Rule 4: IMPOSSIBLE_EFFICIENCY - only if we have efficiency data
  if (efficiency !== null && expectedEff > 0 && efficiency < FRAUD_CONFIG.EFF_RATIO_MIN * expectedEff) {
    flags.push('IMPOSSIBLE_EFFICIENCY');
    score += FRAUD_WEIGHTS.IMPOSSIBLE_EFFICIENCY;
  }
  
  // Rule 5: TANK_OVERFLOW_MATH - only if we have fuel estimate
  if (transaction.estFuelL !== null && transaction.estFuelL !== undefined && 
      tankCapacity > 0 && transaction.estFuelL > tankCapacity * 1.1) {
    flags.push('TANK_OVERFLOW_MATH');
    score += FRAUD_WEIGHTS.TANK_OVERFLOW_MATH;
  }
  
  // Rule 6: SMALL_FILLS_FREQUENT_24H - require both count and sum thresholds
  if (rollingWindows.smallCount24h >= FRAUD_CONFIG.SMALL_FILL_MIN_COUNT_24H &&
      rollingWindows.smallSum24h >= FRAUD_CONFIG.SMALL_FILL_MIN_SUM_24H) {
    flags.push('SMALL_FILLS_FREQUENT_24H');
    score += FRAUD_WEIGHTS.SMALL_FILLS_FREQUENT_24H;
  }
  
  // Rule 7: SMALL_FILLS_ACCUMULATE_24H - separate from frequent (removed to avoid double flagging)
  // This is now combined with Rule 6 above
  
  // Rule 8: SMALL_FILLS_SAME_STATION
  if (rollingWindows.smallSameStation12h) {
    flags.push('SMALL_FILLS_SAME_STATION');
    score += FRAUD_WEIGHTS.SMALL_FILLS_SAME_STATION;
  }
  
  // Rule 9: FILL_FREQUENCY_SPIKE_24H
  if (rollingWindows.fillsIn24h > FRAUD_CONFIG.MAX_FILLS_PER_24H) {
    flags.push('FILL_FREQUENCY_SPIKE_24H');
    score += FRAUD_WEIGHTS.FILL_FREQUENCY_SPIKE_24H;
  }
  
  return { flags, score };
}

// Get fraud flag display names in Arabic
export function getFraudFlagDisplayName(flag) {
  const displayNames = getConfigValue('display.FLAG_NAMES_AR', {});
  return displayNames[flag] || flag;
}

// Get fraud score color class with better thresholds
export function getFraudScoreColor(score) {
  const colors = getConfigValue('display.SCORE_COLORS', {});
  
  if (score >= 5) return colors.HIGH || 'bg-red-100 text-red-800 border-red-200';
  if (score >= 3) return colors.MEDIUM || 'bg-orange-100 text-orange-800 border-orange-200';
  if (score > 0) return colors.LOW || 'bg-amber-100 text-amber-800 border-amber-200';
  return colors.NONE || 'bg-gray-100 text-gray-800 border-gray-200';
}

// Get transaction row severity for UI styling
export function getTransactionSeverity(score) {
  if (score >= 5) return 'high';
  if (score > 0) return 'low';
  return 'none';
}